import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import ManualJobSearch from './JobSearchManual';

interface GenericList {
    id: number;
    name: string;
}

interface JobList {
    id: number;
    jobTypeId: number;
    quoteNumber: string;
    manualQuoteNumber: string;
    customer: string;
    customerContact: string;
    customerLocked: boolean;
    eventName: string;
    siteAddress: string;
    goAheadOverride: boolean;
    status: string;
    showInvoiceAmount: boolean;
    reckonInvoiceNumber: number;
    invoiceAmountFormatted: string;
    insertedBy: string;
}

const JobSearchData = (props: RouteComponentProps<{}>) => {
    const [jobId, setJobId] = React.useState("");
    const [quoteNumber, setQuoteNumber] = React.useState("");
    const [purchaseOrderNumber, setPurchaseOrderNumber] = React.useState("");
    const [siteAddress, setSiteAddress] = React.useState("");
    const [suburb, setSuburb] = React.useState("");
    const [showCancelledJobs, setShowCancelledJobs] = React.useState(false);
    const [jobStatusId, setJobStatusId] = React.useState(-1);
    const [customerId, setCustomerId] = React.useState(-1);
    const [eventName, setEventName] = React.useState("");
    const [results, setResults] = React.useState<JobList[]>([]);
    const [customers, setCustomers] = React.useState <GenericList[]>([]);
    const [statusList, setStatusList] = React.useState <GenericList[]>([]);
    const [showResults, setShowResults] = React.useState(false);
    const [canCopyJob, setCanCopyJob] = React.useState(false);
    const [canUnlockJob, setCanUnlockJob] = React.useState(false);
    const [showManualJobSearch, setShowManualJobSearch] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //set jobtypeid to 1 to get ALL customers 
        axios.get('api/Job/GetJobSearchDropdowns?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setCustomers(res.data.customers);
            setStatusList(res.data.statusOptions);
            setCanCopyJob(res.data.canCopyJob);
            setCanUnlockJob(res.data.canUnlockJob);
            setLoading(false);
        })
    }

    const handleJobStatusChange = (e: any) => {
        e.preventDefault();

        var value = e.target.value;
        setJobStatusId(value);
    }

    const handleCustomerChange = (e: any) => {
        e.preventDefault();

        var value = e.target.value;
        setCustomerId(value);
    }

    const handleShowInvoiceAmount = (e: any, jobIndex: number) => {
        e.preventDefault();

        getInvoiceAmount(jobIndex);
    }

    const getInvoiceAmount = async (jobIndex: number) => {
        const token = await authService.getAccessToken();

        var jobs = [...results];
        var job = jobs[jobIndex];
        
        axios.get('api/Job/GetSearchInvoiceAmount?JobId=' + job.id, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            job.reckonInvoiceNumber = res.data.reckonInvoiceNumber;
            job.invoiceAmountFormatted = res.data.invoiceTotalFormatted;
            job.showInvoiceAmount = true;
            setResults(jobs);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const handleGoAheadOverride = (e: any, jobIndex: number) => {
        e.preventDefault();

        var jobs = [...results];
        var job = jobs[jobIndex];

        job.goAheadOverride = e.target.checked;
        
        //save job
        saveGoAheadOverride(jobs, job.id, e.target.checked);
    }

    const saveGoAheadOverride = async(jobs: JobList[], jobId: number, goAhead: boolean) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var saveGoAhead = {
            jobId: jobId,
            goAheadOverride: goAhead,
            subId: user.sub
        };

        axios.post('api/Job/SaveGoAheadOverride', saveGoAhead, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
            .then(res => {
                if (res.data.isError) {
                    var errors = res.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    setResults(jobs);
                    toast.success("Job goahead has been saved successfully");
                }
            })
            .catch(error => {
                toast.error(error.message);
            });
    }

    const reset = () => {
        setJobId("");
        setQuoteNumber("");
        setPurchaseOrderNumber("");
        setSiteAddress("");
        setSuburb("");
        setShowCancelledJobs(false);
        setJobStatusId(-1);
        setCustomerId(-1);
        setEventName("");
        setResults([]);
        setShowResults(false);
        setShowManualJobSearch(false);
    }

    const search = (e: any) => {
        e.preventDefault();
        
        searchJobs();
    }

    const searchJobs = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var custId = customerId == -1 ? null : customerId;
        var eventNameSearch = eventName.trim();
        var siteAddrSearch = siteAddress.trim();
        var suburbSearch = suburb.trim();

        var jobIdValid = true;
        if (jobId) {
            var checkJobId = Number(jobId);
            if (Number.isNaN(checkJobId)) {
                setShowResults(false);
                toast.error("Please enter a valid Job No!");
                jobIdValid = false;
                return;
            }
        } else {
            jobIdValid = false;     //no job id entered
        }

        //check to make sure at least one search field has been entered
        if (jobIdValid || custId || eventNameSearch.length > 5 || quoteNumber || siteAddrSearch.length > 3 || suburbSearch.length > 2 || purchaseOrderNumber || jobStatusId > 0) {
            axios.get('api/Job/SearchJobs?SubId=' + user.sub + '&JobId=' + jobId + '&CustomerId=' + custId + '&EventName=' + eventName + '&QuoteNo=' + quoteNumber + '&SiteAddress=' + encodeURIComponent(siteAddress) + '&Suburb=' + suburb + '&PurchaseOrderNumber=' + purchaseOrderNumber + '&StatusId=' + jobStatusId + '&ShowCancelledJobs=' + showCancelledJobs, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                setResults(res.data);
                setShowResults(true);
            })
        } else {
            setShowResults(false);
            toast.error("Please enter at least one search item (Site Addr > 3 characters, Event > 5 characters or Suburb > 2 characters)");
        }
        
    }

    //MANUAL JOB SEARCH
    const openManualJobSearch = () => {
        setShowManualJobSearch(true);
    }

    const closeManualJobSearch = () => {
        setShowManualJobSearch(false);
    }
    //END MANUAL JOB SEARCH

    let manualJobModal = <ManualJobSearch showModal={showManualJobSearch} hideModal={closeManualJobSearch } />

    const renderDetails = (
        <div>
            <div>
                {manualJobModal}
            </div>
            
            <div>
                <div className="makeitflexspacebetween">
                    <div>
                        <label className="input-group" htmlFor="jobId">
                            <span className="label label--setsizesm">Job No</span>
                            <input className="input" type="text" id="jobId" name="jobId" value={jobId} onChange={(e) => setJobId(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="siteAddress">
                            <span className="label label--setsizesm">Site Address</span>
                            <input className="input" type="text" id="siteAddress" name="siteAddress" value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="customer">
                            <span className="label">Customer</span>
                            <select className="select" id="customer" name="customerId" value={customerId} onChange={(e) => handleCustomerChange(e)}>
                                <option defaultValue="-1"></option>
                                {customers.map(cust =>
                                    <option key={cust.id} value={cust.id}>{cust.name}</option>
                                )};
                            </select>
                        </label>
                    </div>
                    <div>
                        <label className="input-group" htmlFor="eventName">
                            <span className="label label--setsizesm">Event Name</span>
                            <input className="input" type="text" id="eventName" name="eventName" value={eventName} onChange={(e) => setEventName(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="suburb">
                            <span className="label label--setsizesm">Suburb</span>
                            <input className="input" type="text" id="suburb" name="suburb" value={suburb} onChange={(e) => setSuburb(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="jobStatusId">
                            <span className="label">Status</span>
                            <select className="select" id="jobStatusId" name="jobStatusId" value={jobStatusId} onChange={(e) => handleJobStatusChange(e)}>
                                <option defaultValue="-1"></option>
                                {statusList.map(status =>
                                    <option key={status.id} value={status.id}>{status.name}</option>
                                )};
                            </select>
                        </label>
                    </div>
                    <div>
                        <label className="input-group" htmlFor="quoteNumber">
                            <span className="label label--setsizesm">Quote Number</span>
                            <input className="input" type="text" id="quoteNumber" name="quoteNumber" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="purchaseOrderNumber">
                            <span className="label label--setsizesm">PO Number</span>
                            <input className="input" type="text" id="purchaseOrderNumber" name="purchaseOrderNumber" value={purchaseOrderNumber} onChange={(e) => setPurchaseOrderNumber(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="showCancelledJobs">
                            <span className="label label--setsizesm">Show Cancelled Jobs</span>
                            <input className="input checkbox__Size" type="checkbox" id="showCancelledJobs" name="showCancelledJobs" checked={showCancelledJobs} onChange={(e) => setShowCancelledJobs(e.target.checked)}></input>
                        </label>
                    </div>
                </div>
                <div className="input-group-parent">
                    <div>
                        <button className="defaultbutton-prev" type='button' onClick={search}>Search</button>
                        <button className="defaultbutton-next" type='button' onClick={reset}>Reset</button>
                    </div>
                </div>
                <div className="input-group-parent">
                    <button className="defaultbutton" type='button' onClick={openManualJobSearch}>Search Manual Jobs</button>
                </div>
                <hr></hr>
                <div className={showResults ? 'overflowAuto' : 'hidden' }>
                    <table className='table tableColours'>
                        <thead>
                            <tr>
                                <th>Job No.</th>
                                <th>Customer</th>
                                <th>Site Address</th>
                                <th>Contact</th>
                                <th>Event</th>
                                <th>Quote No.</th>
                                <th>Status</th>
                                <th>Inserted By</th>
                                <th className="textalignright">Invoice Amt</th>
                                <th className= "textaligncenter">Job GoAhead</th>
                                <th className={canCopyJob ? "textaligncenter" : "hidden"}>Copy Job</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((job, index) =>
                                <tr key={job.id}>
                                    <td>
                                        <Link to={'/job/' + job.id} target="_blank">
                                            {job.id}
                                        </Link>
                                    </td>
                                    <td>{job.customer}</td>
                                    <td>{job.siteAddress}</td>
                                    <td>{job.customerContact}</td>
                                    <td>{job.eventName}</td>
                                    <td>{job.quoteNumber}</td>
                                    <td>{job.status}</td>
                                    <td>{job.insertedBy}</td>
                                    <td className="textalignright">
                                        <a className={job.showInvoiceAmount ? "hidden" : ""} href="#" onClick={(e) => handleShowInvoiceAmount(e, index)}>
                                            Get Inv Amt
                                        </a>
                                        {job.showInvoiceAmount ? "Inv# " + job.reckonInvoiceNumber : "" }
                                        {job.showInvoiceAmount ? " for " + job.invoiceAmountFormatted : ""}
                                    </td>
                                    <td className="textaligncenter">
                                        <input type="checkbox" className="input" checked={job.goAheadOverride} onChange={(e) => handleGoAheadOverride(e, index)} disabled={!canUnlockJob || !job.customerLocked} />
                                    </td>
                                    <td className={canCopyJob ? "" : "hidden"}>
                                        <Link to={'/newjob/' + job.jobTypeId + "/" + job.id}>
                                            <span className="fas fa-copy copy--icon alignIconCenter"></span>
                                        </Link>
                                    </td>
                                    <td>
                                        <Link to={'/job/' + job.id} target="_blank">
                                            Open
                                        </Link>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>Job Search</h1>
            {contents}
        </div>
    );
}

export default JobSearchData;